import * as z from "zod";
import { billingPeriodSchema } from "./get-bills";

const getBillMonthSummariesInput_ = z.object({
  // The month to total, as its start instant (UTC midnight on the 1st) taken
  // verbatim from a returned `months[].monthStart`. Omitted → newest month.
  month: z.date().optional(),
  // Billed-date window to total instead of a whole month ("Custom"). Wins over
  // `month` when both are sent.
  customRange: z.object({ start: z.date(), end: z.date() }).optional(),
});
export const getBillMonthSummariesInput: z.ZodType<GetBillMonthSummariesInput> =
  getBillMonthSummariesInput_;
export interface GetBillMonthSummariesInput extends z.infer<
  typeof getBillMonthSummariesInput_
> {}

const billMonthSchema_ = z.object({
  // UTC month-start instant (midnight on the 1st). Doubles as the stable key
  // the client echoes back as `input.month` to total the month.
  monthStart: z.date(),
});
export const billMonthSchema: z.ZodType<BillMonth> = billMonthSchema_;
export interface BillMonth extends z.infer<typeof billMonthSchema_> {}

const selectedSummarySchema_ = z.object({
  // The totaled window's inclusive bounds — scopes the invoices table with no
  // timezone math, and makes a month and a custom range render identically.
  start: z.date(),
  end: z.date(),
  total: z.number(),
  currency: z.string().default("USD"),
  // Distinct billing cadences across the window's bills (e.g. a bimonthly CRO
  // bill alongside the monthly software cycle). The card joins them.
  cadences: z.array(billingPeriodSchema),
  invoiceCount: z.number(),
  // The window carries the still-open returns bill, whose total is live and
  // still accruing — the card badges it as in progress.
  hasOpenBill: z.boolean(),
});
export const selectedSummarySchema: z.ZodType<SelectedSummary> =
  selectedSummarySchema_;
export interface SelectedSummary extends z.infer<
  typeof selectedSummarySchema_
> {}

const getBillMonthSummariesOutput_ = z.object({
  // Newest month first. Just the picker's option keys — a cheap `bill`-table
  // scan with no money in it.
  months: z.array(billMonthSchema),
  // The one totaled window (`customRange`, else `month`, else newest month). A
  // requested window zeroes rather than nulls; null means no bills at all.
  selected: selectedSummarySchema_.nullable(),
});
export const getBillMonthSummariesOutput: z.ZodType<GetBillMonthSummariesOutput> =
  getBillMonthSummariesOutput_;
export interface GetBillMonthSummariesOutput extends z.infer<
  typeof getBillMonthSummariesOutput_
> {}

export const getBillMonthSummaries = {
  name: "getBillMonthSummaries" as const,
  input: getBillMonthSummariesInput,
  output: getBillMonthSummariesOutput,
};
